"use client";

import { ProgressiveImage } from "@/component/ProgressiveImage";
import { VideoPlayer } from "@/component/VideoPlayer";
import classNames from "classnames";
import Image from "next/image";
import { HTMLAttributes } from "react";
import styles from "./index.module.css";

export interface BlurBackgroundContainerProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  mediaType: "image" | "video";
  hoverScale?: boolean;
  src?: string;
  previewSrc?: string;
  height?: string | number;
  showControls?: boolean;
  showPlayButton?: boolean;
  alt?: string;
  className?: string;
  backgroundBlur?: number;
  backgroundScale?: number;
  onError?: () => void;
}

export function BlurBackgroundContainer({
  mediaType,
  src,
  previewSrc,
  height,
  hoverScale = false,
  showControls = true,
  showPlayButton = true,
  alt = "media",
  className,
  backgroundBlur = 40,
  backgroundScale = 1.1,
  onError,
  ...props
}: BlurBackgroundContainerProps) {
  const isVideo = mediaType === "video";
  const baseSrc = src ?? "";
  const hasPreviewSrc = Boolean(previewSrc);
  const hasSrc = Boolean(baseSrc);

  // 如果没有 src，显示占位状态
  if (!hasSrc) {
    return (
      <div
        className={classNames(
          "relative flex items-center justify-center overflow-hidden bg-gray-200",
          className
        )}
        style={{ height }}
        {...props}
      >
        <Image
          src="/dark-grey-logo.png"
          alt="placeholder"
          width={80}
          height={80}
          className="relative z-[1] object-contain opacity-30"
          draggable={false}
        />
      </div>
    );
  }

  // TypeScript 类型断言：在 hasSrc 检查之后，src 一定不是 undefined
  const safeSrc = baseSrc as string;

  return (
    <div
      key={safeSrc}
      className={classNames(
        "relative flex items-center justify-center overflow-hidden",
        className
      )}
      style={{ height }}
      {...props}
    >
      {/* 背景模糊层 */}
      <div
        className={classNames(
          "absolute inset-0 h-full w-full bg-cover bg-center bg-no-repeat transition-opacity duration-300"
        )}
        style={{
          backgroundImage: hasPreviewSrc
            ? `url(${previewSrc})`
            : isVideo
            ? `url(${safeSrc})`
            : `url(${safeSrc})`,
          filter: `blur(${backgroundBlur}px)`,
          transform: `scale(${backgroundScale})`,
        }}
      >
        {/* 如果是视频且没有预览图，在背景层放置不可播放的视频 */}
        {isVideo && !hasPreviewSrc && (
          <VideoPlayer
            src={safeSrc}
            className={styles.videoPlayer}
            showToolbar={false}
            showControls={false}
            showPlayButton={false}
            disableClickPlay={true}
          />
        )}
      </div>

      {/* 前景内容层 */}
      {isVideo ? (
        <VideoPlayer
          src={safeSrc}
          className={styles.videoPlayer}
          showToolbar={false}
          showControls={showControls}
          showPlayButton={showPlayButton}
        />
      ) : (
        <div className="absolute inset-0 z-[1] flex h-full w-full">
          <ProgressiveImage
            key={safeSrc}
            src={safeSrc}
            alt={alt}
            fill
            containerClassName="flex-1"
            className={classNames("object-contain", {
              "hover:transform hover:scale-125 transition-transform duration-300":
                hoverScale,
            })}
            skeletonClassName="z-[2]"
            skeletonTestId="blur-background-skeleton"
            draggable={false}
            sizes="(max-width: 768px) 100vw, 50vw"
          />
        </div>
      )}
    </div>
  );
}
